import { flexRender, Table } from "@tanstack/react-table";
import { SelectInput } from "@/components/select-input/select-input";
import { MultiValue, SingleValue } from "react-select";
import { cn, isDefined } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { DebouncedInput } from "../debounced-input";
import React from "react";
import { CircleButton } from "../circle";
import { Loader } from "../loader";
import { TbRefresh } from "react-icons/tb";
import { TableTotal, TableCount } from "./data-table-context";
import { TableGalleryToggle } from "./data-table-gallery-toggle";
import { useTooltipProps } from "../tooltip";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AccordionControls } from "./data-table-accordion";
import {
  useTableAccordionGrouping,
  useTableColumns,
  useTableSettings,
} from "./data-table-context";
import { DataTableActiveFilters } from "./data-table-active-filters";
import { ColumnManager } from "./table-settings/column-manager";

export type ShowPaginationOptions = {
  showAllSelected?: boolean;
  showPagination?: boolean;
  showPerPage?: boolean;
  showRefresh?: boolean;
};

export type DataTablePaginationProps<TData> = {
  table: Table<TData>;
  total?: TableTotal;
  count?: TableCount;
  itemType?: string;
  pageOptions?: number[];
  loading?: boolean;
  refresh?: () => void;
  top?: React.FC;
  additional?: React.FC;
  ShowPaginationOptions?: ShowPaginationOptions;
  disableFilterBreadcrumbs?: boolean;
};
const getOption = <T,>(value: T) => ({ value, label: `${value}` });
const isMultiValue = <T,>(
  option: MultiValue<T> | SingleValue<T>,
): option is MultiValue<T> => Array.isArray(option);
const getSingle = <T,>(option: MultiValue<T> | SingleValue<T>) =>
  isMultiValue(option) ? option?.[0] : option;

// const paginate = (currentPage: number, lastPage: number | undefined) => {
//   const delta = 1;
//   const range: (number | "...")[] = [];

//   const startNum = Math.max(2, currentPage - delta);
//   const endNum =
//     lastPage !== undefined
//       ? Math.min(lastPage - 1, currentPage + delta)
//       : undefined;

//   for (let i = startNum; i <= (endNum ?? currentPage + 1); i++) {
//     range.push(i);
//   }

//   if (currentPage - delta > 2) {
//     range.unshift("...");
//   }

//   if (lastPage === undefined || currentPage + delta < lastPage - 1) {
//     range.push("...");
//   }

//   range.unshift(1);
//   if (lastPage !== undefined && lastPage > 1) {
//     range.push(lastPage);
//   }
//   return range;
// };

const DataTablePagination_ = <TData,>(
  {
    table,
    total,
    count,
    itemType = "items",
    pageOptions: _pageOptions,
    loading,
    refresh,
    additional: Additional,
    top: Top,
    disableFilterBreadcrumbs,
    className,
    ShowPaginationOptions,
    ...rest
  }: DataTablePaginationProps<TData> &
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const showPerPage = ShowPaginationOptions?.showPerPage ?? true;
  const showRefresh = ShowPaginationOptions?.showRefresh ?? true;
  const showAllSelected = ShowPaginationOptions?.showAllSelected ?? false;
  const showPagination = ShowPaginationOptions?.showPagination ?? true;

  const pageOptions = React.useMemo(
    () => (_pageOptions ?? [10, 20, 30, 40, 50]).map(getOption),
    [_pageOptions],
  );
  const pageOption = getOption(table.getState().pagination.pageSize);
  const page = table.getState().pagination.pageIndex + 1;
  const lastPage =
    (total?.isEstimate ? undefined : table.getPageCount()) || undefined;
  const selected = React.useMemo(
    () =>
      Object.entries(table.getState().rowSelection).reduce(
        (prev, [_id, selected]) => prev + (selected ? 1 : 0),
        0,
      ),
    [table.getState().rowSelection],
  );
  const tooltipProps = useTooltipProps();
  const { t } = useTranslation();
  const tableSettings = useTableSettings();
  const selectColumn =
    table.isGalleryEnabled() && table.getState().gallery
      ? table.getColumn("select")
      : undefined;
  return (
    <div
      data-component="data-table-pagination"
      {...rest}
      ref={ref}
      className={cn(
        "w-full flex flex-col pb-2 font-medium text-sm text-muted-foreground @container/pagination gap-4",
        className,
      )}
    >
      {Top && (
        <div className="flex items-center gap-2 supports-[container]:gap-1 supports-[container]:@md/pagination:gap-2">
          <Top />
        </div>
      )}
      <DataTableActiveFilters disable={disableFilterBreadcrumbs} />
      <div
        data-component="data-table-pagination-row"
        className="flex items-center gap-2 supports-[container]:gap-1 supports-[container]:@md/pagination:gap-2"
      >
        <div
          className={cn(
            "flex items-center gap-2 supports-[container]:gap-1 supports-[container]:@md/pagination:gap-2",
          )}
        >
          {selectColumn && (
            <span>
              {flexRender(selectColumn.columnDef.header, {
                column: selectColumn,
                table,
                header: undefined as any,
              })}
            </span>
          )}
          {total !== undefined && (
            <span>
              {`${total?.isEstimate ? "~" : ""}${total?.value !== undefined ? total.value.toLocaleString("en-US") : ""}${count?.value && count?.value !== total?.value ? ` of ${count?.isEstimate ? "~" : ""}${count.value.toLocaleString("en-US")}` : ""}${itemType ? ` ${itemType}` : ""}`}
              {!table.getAllSelected() && selected > 0 && ", "}
            </span>
          )}
          {!table.getAllSelected() && selected > 0 && (
            <span>{selected} Selected</span>
          )}
          {showAllSelected && (
            <AllSelected table={table} itemType={itemType} total={total} />
          )}
          {Additional && <Additional />}
        </div>
        {showPagination && (
          <div
            className={cn(
              "ml-auto flex items-center pr-2",
              "gap-4 supports-[container]:gap-2 supports-[container]:@md/pagination:gap-4",
            )}
          >
            <div
              className={cn(
                "flex items-center",
                "gap-2 supports-[container]:gap-1 supports-[container]:@md/pagination:gap-2",
              )}
            >
              {!!table.isGalleryEnabled() && (
                <TableGalleryToggle table={table} />
              )}
              <AccordionControlsWrapper />
              {showRefresh && refresh && (
                <CircleButton
                  data-component="data-table-refresh"
                  size={"sm"}
                  type="button"
                  onClick={refresh}
                  {...tooltipProps}
                  data-tooltip-content={t("refresh")}
                >
                  {loading ? (
                    <Loader className="size-5" />
                  ) : (
                    <TbRefresh className="size-5 -scale-x-100" />
                  )}
                </CircleButton>
              )}
              {showPerPage && (
                <>
                  <p className="supports-[container]:hidden supports-[container]:@2xl/pagination:block">
                    Rows per page
                  </p>
                  <SelectInput
                    data-component="data-table-pagination-page-select"
                    value={pageOption}
                    options={pageOptions}
                    isMulti={false}
                    className="text-foreground"
                    classNames={{
                      control: (_s) => cn("h-7 min-w-[4.5rem]"),
                      valueContainer: (_s) => cn("min-w-auto"),
                    }}
                    onChange={(option) => {
                      const value = getSingle(option)?.value;
                      if (isDefined(value)) table.setPageSize(value);
                    }}
                  />
                </>
              )}
            </div>
            <div
              data-component="data-table-pagination-text"
              className={cn(
                "flex items-center font-medium",
                "gap-2 supports-[container]:gap-1 supports-[container]:@md/pagination:gap-2",
              )}
            >
              <span>Page</span>
              {total?.isEstimate ? (
                <span>{page}</span>
              ) : (
                <DebouncedInput
                  id="pagination-page-input"
                  data-component="data-table-pagination-page-input"
                  type="number"
                  min={1}
                  max={lastPage}
                  isNullable={false}
                  isUndefinable={false}
                  onChange={(v) => table.setPageIndex(v - 1)}
                  debounce={300}
                  value={page}
                  className="min-w-12 h-7 pr-1 w-[calc((var(--length)*9px)+4px+14px)] text-foreground"
                  style={{ "--length": `${String(page).length + 2}` } as any}
                />
              )}
              {!total?.isEstimate && (
                <span
                  className={cn(
                    "min-w-8 supports-[container]:hidden supports-[container]:@md/pagination:block",
                    !count?.value &&
                      "supports-[container]:@xs/pagination:block",
                  )}
                >
                  of {lastPage}
                </span>
              )}
            </div>
            <div>
              <Pagination>
                <PaginationContent
                // className={cn(
                //   (lastPage ?? 0) >= 5
                //     ? "@xl/pagination:w-64"
                //     : lastPage == 4
                //       ? "@xl/pagination:w-48"
                //       : undefined,
                // )}
                >
                  <PaginationItem data-component="data-table-pagination-previous">
                    <PaginationPrevious
                      disabled={!table.getCanPreviousPage()}
                      type="button"
                      onClick={(_ev) => table.previousPage()}
                    />
                  </PaginationItem>
                  {/* {paginate(page, total?.isEstimate ? undefined : lastPage).map(
                  (v, i) =>
                    v === "..." ? (
                      <PaginationItem
                        className="supports-[container]:hidden supports-[container]:@xl/pagination:block"
                        key={i}
                      >
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem
                        className="supports-[container]:hidden supports-[container]:@xl/pagination:block"
                        key={i}
                      >
                        <PaginationButton
                          isActive={v === page}
                          className={cn(v === page && "text-foreground")}
                          onClick={(_ev) => table.setPageIndex(v - 1)}
                        >
                          {v}
                        </PaginationButton>
                      </PaginationItem>
                    ),
                )} */}
                  <PaginationItem data-component="data-table-pagination-next">
                    <PaginationNext
                      disabled={!total?.isEstimate && !table.getCanNextPage()}
                      type="button"
                      onClick={(_ev) => table.nextPage()}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <ColumnManager />
          </div>
        )}
        {!showPagination && tableSettings?.enabled && (
          <div className="ml-auto flex items-center pr-2">
            <ColumnManager />
          </div>
        )}
      </div>
    </div>
  );
};

const AccordionControlsWrapper = () => {
  const columns = useTableColumns();
  const accordionGrouping = useTableAccordionGrouping();

  // Only show accordion controls if grouping is enabled (groupByColumn is set)
  if (
    !columns ||
    columns.length === 0 ||
    !accordionGrouping.state.groupByColumn
  )
    return null;

  return (
    <div className="flex items-center gap-1">
      <AccordionControls
        columns={columns}
        groupByColumn={accordionGrouping.state.groupByColumn}
        onGroupByColumnChange={accordionGrouping.actions.setGroupByColumn}
        onExpandAll={accordionGrouping.actions.expandAllGroups}
        onCollapseAll={accordionGrouping.actions.collapseAllGroups}
        hasGroups={accordionGrouping.state.expandedGroups.size > 0}
      />
    </div>
  );
};

export const DataTablePagination = React.forwardRef(
  DataTablePagination_,
) as typeof DataTablePagination_;

const AllSelected = <TData,>({
  table,
  itemType,
  total,
}: {
  table: Table<TData>;
  itemType: string;
  total?: TableTotal;
}) => {
  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
  const isAllSelected = table.getAllSelected();

  return (
    <>
      {(isAllPageRowsSelected || isAllSelected) &&
        (isAllSelected ? (
          <span>
            <Button
              variant={"link"}
              type="button"
              onClick={() => {
                table.setAllSelected(false);
                table.resetRowSelection();
              }}
            >{`Deselect all ${total?.value} ${itemType} from all pages`}</Button>
          </span>
        ) : (
          <span>
            <Button
              variant={"link"}
              type="button"
              onClick={() => {
                table.setAllSelected(true);
              }}
            >{`Select all ${total?.value} ${itemType} from all pages`}</Button>
          </span>
        ))}
    </>
  );
};
